import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  Badge,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  Flex,
  Icon,
  Skeleton,
  Text,
  toast,
} from '@kognitos/lattice';

import type { V2BookConnectionDescriptor } from '@/generated/hooks/bdk/models';
import { useRetrieveAllWorkspaceBookConnections } from '@/generated/hooks/bdk/v2/services/library';
import {
  getListSecretsQueryKey,
  useDeleteSecret,
  useDeleteSecret2,
} from '@/generated/hooks/gringotts/v1/services/secret_service';
import { captureException } from '@/modules/sentry';

import { ECredentialScope, type TCredential } from '../types';
import { getCredentialScope } from '../utils';

/** One page covers a workspace's connections using a single credential. */
const USAGE_PAGE_SIZE = 100;

/** Connections shown as cards; the rest are counted. */
const SHOWN_CONNECTIONS = 6;

const hasLabel = (connection: V2BookConnectionDescriptor, key: string) =>
  !!connection.labels?.some(
    label => label.key === key && label.value === 'true',
  );

/** Props for {@link DeleteCredentialDialog}. */
export interface IDeleteCredentialDialogProps {
  organizationId: string;
  workspaceId: string;
  /** The credential to delete; `null` keeps the dialog closed. */
  credential: TCredential | null;
  onClose: () => void;
  /** Called once the credential is deleted. */
  onDeleted: (credentialId: string) => void;
  /**
   * The integration's display name. A credential belongs to one integration's
   * auth method, so every connection using it is of that integration.
   */
  integrationDisplayName?: string;
  /** The integration's logo, as an image URL. */
  integrationIconSrc?: string;
}

/**
 * Confirms deleting a credential, warning about the connections that use it.
 * Only this workspace's connections can be listed (an organization-wide
 * credential may be used elsewhere), so the list is a lower bound.
 *
 * @param props - Component props.
 * @returns The confirmation dialog.
 */
export function DeleteCredentialDialog({
  organizationId,
  workspaceId,
  credential,
  onClose,
  onDeleted,
  integrationDisplayName,
  integrationIconSrc,
}: IDeleteCredentialDialogProps) {
  const { t } = useTranslation('credentials');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();
  const deleteOrgSecret = useDeleteSecret();
  const deleteWorkspaceSecret = useDeleteSecret2();

  const credentialId = credential?.id ?? '';
  const isOrganizationWide =
    !!credential &&
    getCredentialScope(credential) === ECredentialScope.Organization;

  const usageQuery = useRetrieveAllWorkspaceBookConnections(
    organizationId,
    workspaceId,
    {
      filter: `credentials_id = "${credentialId}"`,
      page_size: USAGE_PAGE_SIZE,
    },
    { query: { enabled: !!credentialId } },
  );
  const usedBy = usageQuery.data?.data.book_connections ?? [];
  const isPending =
    deleteOrgSecret.isPending || deleteWorkspaceSecret.isPending;

  const environmentOf = (connection: V2BookConnectionDescriptor) => {
    const isTest = hasLabel(connection, 'draft_connection');
    const isProduction = hasLabel(connection, 'published_connection');
    if (isTest && isProduction) return t('credentials.delete.environment.both');
    if (isProduction) return t('credentials.delete.environment.production');
    return isTest ? t('credentials.delete.environment.test') : undefined;
  };

  const handleDelete = async () => {
    if (!credential) return;
    try {
      if (isOrganizationWide) {
        await deleteOrgSecret.mutateAsync({ organizationId, id: credentialId });
      } else {
        await deleteWorkspaceSecret.mutateAsync({
          organizationId,
          workspaceId,
          id: credentialId,
        });
      }
      void queryClient.invalidateQueries({
        queryKey: getListSecretsQueryKey(organizationId),
      });
      toast.success(t('credentials.delete.deleted'));
      onDeleted(credentialId);
      onClose();
    } catch (error) {
      captureException(error as Error, {
        tags: { module: 'credentials', action: 'deleteCredential' },
      });
      toast.error(t('credentials.delete.failed'));
    }
  };

  const name = credential?.display_name || credential?.name || '';

  return (
    <AlertDialog open={!!credential} onOpenChange={open => !open && onClose()}>
      <AlertDialogContent data-testid="delete-credential-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('credentials.delete.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('credentials.delete.description', { name })}
            {credential?.oauth_app_id &&
              ` ${t('credentials.delete.oauthNote')}`}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {usageQuery.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <Alert data-testid="delete-credential-usage">
            <Icon type="AlertTriangle" size="sm" />
            <AlertDescription>
              <Flex direction="column" gap={2} width="full">
                <Text level="small">
                  {usageQuery.isError
                    ? t('credentials.delete.checkFailed')
                    : usedBy.length > 0
                      ? t('credentials.delete.inUse', { count: usedBy.length })
                      : t('credentials.delete.notInUse')}
                </Text>
                {usedBy.length > 0 && (
                  <Flex direction="column" gap={2} width="full">
                    {usedBy.slice(0, SHOWN_CONNECTIONS).map(connection => {
                      const environment = environmentOf(connection);
                      return (
                        <Card
                          key={connection.connection_id}
                          className="w-full gap-0 px-3 py-2"
                          data-testid="delete-credential-connection"
                        >
                          <Flex
                            align="center"
                            justify="between"
                            gap={2}
                            width="full"
                          >
                            <Flex direction="column" className="min-w-0 flex-1">
                              <Text
                                level="small"
                                weight="medium"
                                className="block truncate"
                              >
                                {connection.connection_name ||
                                  connection.connection_id}
                              </Text>
                              <Flex align="center" gap={1} className="min-w-0">
                                {integrationIconSrc && (
                                  <img
                                    src={integrationIconSrc}
                                    alt={integrationDisplayName}
                                    className="size-3.5 shrink-0 object-contain"
                                    data-testid="delete-credential-integration-icon"
                                  />
                                )}
                                <Text
                                  level="small"
                                  color="muted"
                                  className="block truncate"
                                >
                                  {integrationDisplayName ||
                                    connection.book_name}
                                </Text>
                              </Flex>
                            </Flex>
                            {environment && (
                              <Badge variant="outline" className="shrink-0">
                                {environment}
                              </Badge>
                            )}
                          </Flex>
                        </Card>
                      );
                    })}
                  </Flex>
                )}
                {usedBy.length > SHOWN_CONNECTIONS && (
                  <Text level="small" color="muted">
                    {t('credentials.delete.andMore', {
                      count: usedBy.length - SHOWN_CONNECTIONS,
                    })}
                  </Text>
                )}
                {/* Only one page of this workspace's connections is checked. */}
                {!usageQuery.isError &&
                  (isOrganizationWide || usedBy.length >= USAGE_PAGE_SIZE) && (
                    <Text level="small" color="muted">
                      {t('credentials.delete.mayBeMore')}
                    </Text>
                  )}
              </Flex>
            </AlertDescription>
          </Alert>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={isPending}>
            {tCommon('common.action.cancel')}
          </AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            disabled={usageQuery.isLoading || isPending}
            isLoading={isPending}
            onClick={() => void handleDelete()}
          >
            {t('credentials.delete.confirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
